import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Upload, FileText, Check, ExternalLink, Calendar, Inbox } from "lucide-react";
import { toast } from "sonner";
import ClaimTracker from "@/components/ClaimTracker";

const STAGES = ["New Intake", "Consult Scheduled", "Active Strategy",
                "Gathering Evidence", "Ready to File", "Filed and Waiting",
                "Decision Received"];

export default function Portal() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [claims, setClaims] = useState([]);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("Lay Statements");
  const fileInput = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [{ data: portal }, { data: cls }] = await Promise.all([
        api.get("/portal/me"),
        api.get("/portal/claims").catch(() => ({ data: [] })),
      ]);
      setData(portal);
      setClaims(cls || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Portal unavailable");
    }
  };
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    load();
  }, [user, loading]); // eslint-disable-line

  const toggleTask = async (t) => {
    await api.patch(`/portal/tasks/${t.task_id}`, { done: !t.done });
    load();
  };

  const onUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("File must be under 25 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      await api.post("/portal/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${f.name} uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  if (loading || (user && !data && !error)) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (error) return (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center">
      <Inbox className="w-14 h-14 text-primary/40 mx-auto mb-6" strokeWidth={1.4} />
      <h1 className="font-display text-3xl">Your portal is almost ready.</h1>
      <p className="mt-3 text-primary/70">{error}. Complete your intake to activate your portal.</p>
      <Button asChild className="mt-8"><Link to="/intake">Start intake →</Link></Button>
    </div>
  );

  const v = data.veteran;
  const currentStageIdx = Math.max(0, STAGES.indexOf(v.status));

  const filesByCat = {};
  (data.categories || []).forEach((c) => (filesByCat[c] = []));
  (data.files || []).forEach((f) => {
    const c = f.kind && filesByCat[f.kind] ? f.kind : "Other";
    filesByCat[c].push(f);
  });

  return (
    <div>
      {/* Header */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
          <div className="label-caps text-accent mb-3">My Portal</div>
          <h1 className="font-display text-3xl lg:text-5xl">
            Welcome back, {v.first_name || v.full_name?.split(" ")[0]}.
          </h1>
          <p className="mt-3 text-primary-foreground/80 max-w-2xl">
            This is your private workspace. Upload documents, follow your progress, and complete tasks assigned by Pamela.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 grid lg:grid-cols-12 gap-8">

        {/* Claim trackers (one per active claim, HLR skips Phase 2) */}
        <div className="lg:col-span-12">
          <Card className="p-6 lg:p-8 border-2" data-testid="portal-status">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="label-caps">My Claim Progress</div>
                <p className="text-xs text-primary/60 mt-1">Live tracker, updated by Pamela as we move through your strategy.</p>
              </div>
              {claims.length > 0 && (
                <span className="font-mono text-xs text-primary/60">{claims.length} claim{claims.length === 1 ? "" : "s"}</span>
              )}
            </div>

            {claims.length === 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="stage-tracker">
                {STAGES.map((s, i) => (
                  <div key={s} className={`p-3 border-2 text-center ${i < currentStageIdx ? "bg-secondary border-primary/30 text-primary/60" : i === currentStageIdx ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-primary/40"}`}>
                    <div className="text-[10px] font-mono mb-1">STAGE {i + 1}</div>
                    <div className="text-xs font-semibold leading-tight">{s}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-10" data-testid="portal-claims">
                {claims.map((c, i) => (
                  <div key={c.claim_id} className={i > 0 ? "pt-8 border-t-2 border-border" : ""}>
                    <div className="flex items-end justify-between flex-wrap gap-2 mb-5">
                      <div>
                        <div className="label-caps text-primary/70">Claim</div>
                        <div className="font-display text-2xl mt-1">{c.claim_type}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <a
                          href={`/portal/claim/${c.claim_id}/print`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-primary hover:underline underline-offset-4"
                          data-testid={`claim-download-${c.claim_id}`}
                        >
                          Download PDF ↗
                        </a>
                        <span className="px-3 py-1 text-xs font-mono uppercase tracking-widest bg-secondary border-2 border-border">
                          {c.current_status}
                        </span>
                      </div>
                    </div>
                    <ClaimTracker claim={c} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Tasks */}
        <div className="lg:col-span-7">
          <Card className="p-6 lg:p-8 border-2" data-testid="portal-tasks">
            <div className="label-caps mb-4">Next Steps</div>
            {data.tasks.length === 0 ? (
              <div className="text-center py-8 text-primary/60 border-2 border-dashed border-border">
                No tasks yet. Pamela will add tasks after your consult.
              </div>
            ) : (
              <ul className="space-y-3">
                {data.tasks.map((t) => (
                  <li key={t.task_id} className={`flex items-start gap-4 p-4 border-2 ${t.done ? "bg-secondary border-secondary" : "bg-white border-border"}`} data-testid={`task-${t.task_id}`}>
                    <button onClick={() => toggleTask(t)}
                      className={`mt-0.5 w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 ${t.done ? "bg-primary border-primary" : "border-primary hover:bg-secondary"}`}
                      aria-label="toggle done" data-testid={`task-toggle-${t.task_id}`}>
                      {t.done && <Check className="w-4 h-4 text-primary-foreground" strokeWidth={3} />}
                    </button>
                    <div className="flex-1">
                      <div className={`font-semibold ${t.done ? "line-through text-primary/50" : ""}`}>{t.title}</div>
                      {t.detail && <div className="text-sm text-primary/70 mt-1 whitespace-pre-wrap">{t.detail}</div>}
                      {t.due_date && <div className="text-xs text-primary/50 mt-2 font-mono">Due: {t.due_date}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Consult */}
        <div className="lg:col-span-5">
          <Card className="p-6 lg:p-8 border-2 h-full flex flex-col" data-testid="portal-consult">
            <div className="label-caps mb-4">My Consult</div>
            <div className="flex items-start gap-4">
              <Calendar className="w-8 h-8 text-primary" strokeWidth={1.5} />
              <div>
                <div className="font-display text-xl">Book or reschedule</div>
                <p className="text-sm text-primary/70 mt-1">Free 20-minute strategy call with Pamela.</p>
              </div>
            </div>
            {data.calendly_url ? (
              <Button asChild className="mt-6" size="lg" data-testid="portal-calendly">
                <a href={data.calendly_url} target="_blank" rel="noreferrer">
                  Open scheduler <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            ) : (
              <div className="mt-6 text-sm text-primary/60 bg-secondary p-4">Scheduler link coming soon.</div>
            )}
          </Card>
        </div>

        {/* Documents */}
        <div className="lg:col-span-12">
          <Card className="p-6 lg:p-8 border-2" data-testid="portal-documents">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <div className="label-caps">My Documents</div>
                <p className="text-sm text-primary/70 mt-2">PDF, JPG, PNG, DOCX. Max 25 MB per file. Only you and Pamela can see these.</p>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Category</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="mt-1 block h-11 px-3 border-2 border-primary bg-white font-semibold"
                    data-testid="upload-category">
                    {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <label className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold h-11 px-5 cursor-pointer hover:bg-accent/90" data-testid="upload-button">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload"}
                  <input ref={fileInput} type="file" hidden onChange={onUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.docx,application/pdf,image/*" data-testid="upload-input" />
                </label>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data.categories || []).map((cat) => (
                <div key={cat} className="border-2 border-border p-4" data-testid={`cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-semibold text-sm">{cat}</div>
                    <span className="font-mono text-xs text-primary/50">{filesByCat[cat].length}</span>
                  </div>
                  {filesByCat[cat].length === 0 ? (
                    <div className="text-xs text-primary/40 py-3">No files.</div>
                  ) : (
                    <ul className="space-y-2">
                      {filesByCat[cat].map((f) => (
                        <li key={f.file_id}>
                          <a href={`${API}/portal/files/${encodeURI(f.storage_path)}`} target="_blank" rel="noreferrer"
                             className="flex items-center gap-2 text-xs text-primary hover:underline">
                            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate flex-1">{f.original_filename}</span>
                            {f.uploaded_by === "veteran" && <span className="text-[9px] font-mono text-primary/40">YOU</span>}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
